import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router-dom';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import CustomDatePicker from './CustomDatePicker';
import MenuButton from './MenuButton';
import ColorModeIconDropdown from '../theme/ColorModeIconDropdown';
import UserMenu from './UserMenu';
import { useHeaderTitleContext } from './HeaderTitleContext';
import { APP_PATHS } from '../auth/role-based-access-control';

const ROUTE_TITLES: Array<{ path: string; exact?: boolean; title: string }> = [
  { path: APP_PATHS.DASHBOARD, exact: true, title: 'Support Portal Dashboard' },
  { path: APP_PATHS.CLIENT_CHANGES_LOG, exact: true, title: 'Contact Updates Logs' },
  { path: APP_PATHS.CLIENT_LOOKUP, exact: true, title: 'Client Lookup' },
  { path: APP_PATHS.TRANSACTIONS_DEPOSITS, exact: true, title: 'Deposits' },
  { path: APP_PATHS.TRANSACTIONS_WITHDRAWALS, exact: true, title: 'Withdrawals' },
  { path: APP_PATHS.TRANSACTIONS_AUDITS, exact: true, title: 'Transaction Logs' },
  { path: APP_PATHS.RECONCILIATION, exact: true, title: 'Reconciliation' },
  { path: APP_PATHS.AUDIT_LOGS, exact: true, title: 'All Audit Logs' },
  { path: APP_PATHS.BULK_SMS_HISTORY, exact: true, title: 'Delivery Status' },
  { path: APP_PATHS.BULK_SMS, exact: true, title: 'Bulk SMS' },
];

function getDefaultTitle(pathname: string): string | null {
  const match = ROUTE_TITLES.find((r) =>
    r.exact ? pathname === r.path : pathname.startsWith(r.path)
  );
  return match?.title ?? null;
}

export default function Header() {
  const { title, subtitle } = useHeaderTitleContext();
  const location = useLocation();
  const resolvedTitle = title ?? getDefaultTitle(location.pathname);

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        width: '100%',
        maxWidth: { sm: '100%', md: '1700px' },
        mt: 1.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid #5136F2',
        bgcolor: 'background.paper',
        px: 3,
        py: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {resolvedTitle && (
            <Typography component="h1" variant="h5" fontWeight={800} noWrap>
              {resolvedTitle}
            </Typography>
          )}
        </Box>
        <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
          <CustomDatePicker />
          <ColorModeIconDropdown />
          <MenuButton showBadge aria-label="Open notifications">
            <NotificationsRoundedIcon />
          </MenuButton>
          <UserMenu />
        </Stack>
      </Stack>
    </Box>
  );
}
